import { useEffect, useMemo, useRef, useState } from 'react';
import type { HTMLProps, MouseEvent } from 'react';

import type { ErrorSchema } from '@rjsf/utils';
import { z } from 'zod';

import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger
} from '@/shadcn/hover-card';
import { cn } from '@/shadcn/lib/utils';

import { getReadableRJSFErrors } from '@/components/forms/components/form/rjsf.utils';
import type {
  TableColumnTransformed,
  TableRow
} from '@/components/ui/table/table.type';

type Props = {
  rowErrorSchema: ErrorSchema<TableRow>;
  columns: TableColumnTransformed[];
};

const COOLDOWN_MS = 250;

const ErrorSchema = z.object({
  __errors: z.array(z.string()).min(1)
});

export const RowErrorIndicator = ({ rowErrorSchema, columns }: Props) => {
  const [isHovering, setIsHovering] = useState<boolean>(false);
  const isInCooldown = useRef<boolean>(false);
  const cooldownTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);

  useEffect(
    () => () => {
      if (cooldownTimeout.current) {
        clearTimeout(cooldownTimeout.current);
      }
    },
    []
  );

  const handleMouseEnter = () => {
    if (isInCooldown.current) return;
    setIsHovering(true);
  };

  const handleMouseLeave = (event: MouseEvent<HTMLDivElement>) => {
    const nextTarget = event.relatedTarget as HTMLElement | null;

    if (
      nextTarget &&
      (triggerRef.current?.contains(nextTarget) ||
        contentRef.current?.contains(nextTarget))
    ) {
      return;
    }

    setIsHovering(false);
    isInCooldown.current = true;

    if (cooldownTimeout.current) {
      clearTimeout(cooldownTimeout.current);
    }

    cooldownTimeout.current = setTimeout(() => {
      isInCooldown.current = false;
      cooldownTimeout.current = null;
    }, COOLDOWN_MS);
  };

  const errorLength = Object.values(rowErrorSchema).reduce((length, value) => {
    if (Array.isArray(value)) {
      return length + value.length;
    }

    if (value && typeof value === 'object' && value.__errors) {
      return length + value.__errors.length;
    }

    return length;
  }, 0);

  const rowSpecificErrors = rowErrorSchema.__errors ?? [];

  const rowColumnErrors = useMemo(
    () =>
      Object.entries(rowErrorSchema)
        .map(([key, value]) => {
          if (key === '__errors') return;
          const parsedErrors = ErrorSchema.safeParse(value);
          if (parsedErrors.success) {
            const errors = parsedErrors.data.__errors.map(message =>
              getReadableRJSFErrors({ message, propertyName: key })
            );
            return [key, errors];
          }
          return;
        })
        .filter(Boolean) as [string, string[]][],
    [rowErrorSchema]
  );

  return (
    <HoverCard closeDelay={0} open={isHovering} openDelay={75}>
      <HoverCardTrigger asChild data-testid="tooltip-wrapper">
        <div
          className={cn(
            'group absolute top-0 left-0 flex h-full w-[5px] flex-col items-center justify-center bg-destructive pb-2 transition-all ease-out',
            isHovering && 'w-full'
          )}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          ref={triggerRef}
        >
          <div
            className={cn(
              'hidden size-full select-none flex-col items-center justify-center text-destructive-foreground opacity-0 transition-none delay-75 duration-75 ease-out',
              isHovering && 'flex opacity-100 transition-all'
            )}
          >
            {isHovering && (
              <>
                <span className="font-bold text-lg">{errorLength}</span>
                <span className="font-semibold leading-2">
                  {`error${errorLength !== 1 ? 's' : ''}`}
                </span>
              </>
            )}
          </div>
        </div>
      </HoverCardTrigger>
      <HoverCardContent
        align="start"
        className="min-w-[60px] max-w-[440px] rounded-tl-none border-0 p-0 outline-0 data-[state=closed]:animate-none"
        side="right"
        sideOffset={-1}
      >
        <div
          className="flex w-full flex-col gap-4 px-5 py-2.5"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          ref={contentRef}
        >
          <div className="border-border border-b py-2 font-semibold text-sm">
            ⚠️ This row has {errorLength} error{errorLength !== 1 ? 's' : ''}
          </div>
          <div className="text-justify text-[13px]">
            <section className="flex flex-col gap-3">
              {rowSpecificErrors.map((message, i) => (
                <pre
                  className="whitespace-pre-wrap text-start font-normal font-sans"
                  key={i}
                >
                  <KeyBadge
                    className="border-secondary/20 bg-secondary/5 text-secondary/80"
                    itemName="This row"
                  />
                  {` ${message}.`}
                </pre>
              ))}

              {rowColumnErrors.map(([key, messages], i) => {
                if (messages.length === 1) {
                  return (
                    <pre
                      className="whitespace-pre-wrap text-start font-normal font-sans"
                      key={i}
                    >
                      <KeyBadge columnKey={key} columns={columns} />
                      {messages[0]}
                    </pre>
                  );
                }
                return (
                  <div className="mb-1" key={key}>
                    <p className="inline-flex items-center gap-2 font-semibold">
                      In the column
                      <KeyBadge columnKey={key} columns={columns} />
                    </p>
                    <ul>
                      {messages.map((message, index) => (
                        <li className="ml-6 list-disc text-start" key={index}>
                          <pre className="whitespace-pre-wrap font-normal font-sans">
                            The cell{message}
                          </pre>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </section>

            {errorLength === 0 && (
              <p className="font-semibold">No detailed error message</p>
            )}
          </div>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
};

type KeyBadgeProps = (
  | {
      columnKey: string;
      columns: TableColumnTransformed[];
    }
  | {
      itemName: string;
    }
) &
  HTMLProps<HTMLSpanElement>;

const KeyBadge = (props: KeyBadgeProps) => {
  let keyName;
  if ('itemName' in props) {
    keyName = props.itemName;
  } else {
    keyName =
      props.columns.find(col => col.def?.key === props.columnKey)?.def
        ?.header || props.columnKey;
  }

  return (
    <span
      className={cn(
        'mr-1 inline-flex rounded-full border border-primary-deep/20 bg-primary-deep/5 px-2 align-top text-primary-deep/80 dark:border-primary/60 dark:bg-primary/15 dark:text-primary',
        props.className
      )}
    >
      {keyName}
    </span>
  );
};
